import type {
    BoardLock,
    BoardMoveCheck,
    BoardMoveVerdict,
    BoardPlacement,
} from "./Board.types";
import {
    getCellEntries,
    getCellKey,
    type BoardCellEntry,
    type BoardCells,
} from "./boardCells";
import { NO_REFUSALS, type BoardRefusals } from "./boardMoveRules";

/** The selection a drag carries along with the grabbed card. */
export interface BoardDragGroup {
    /** Selected items that move, the grabbed one included. */
    itemIds: ReadonlySet<string>;
    /** Selected items that are locked: they stay where they are. */
    lockedItemIds: ReadonlySet<string>;
    /** Every card keeps its lane: the drop changes columns only. */
    isLaneFixed: boolean;
}

export type BoardMoveRule<TItem> = (
    check: BoardMoveCheck<TItem>,
) => BoardMoveVerdict;

/** An item's cards, in board order; never empty. */
export type BoardItemCopies<TItem> = readonly [
    BoardCellEntry<TItem>,
    ...Array<BoardCellEntry<TItem>>,
];

/** Each item's copies, items in board order (by their first copy). */
export const getCopiesByItem = <TItem>(
    cells: BoardCells<TItem>,
    cellOrder: readonly BoardPlacement[],
    itemIds: ReadonlySet<string>,
) => {
    const copiesByItem = new Map<string, BoardItemCopies<TItem>>();
    cellOrder.forEach((placement) =>
        getCellEntries(cells, placement).forEach((entry) => {
            if (itemIds.has(entry.itemId)) {
                const copies = copiesByItem.get(entry.itemId);
                copiesByItem.set(
                    entry.itemId,
                    copies === undefined ? [entry] : [...copies, entry],
                );
            }
        }),
    );
    return copiesByItem;
};

interface GetBoardDragGroupArgs<TItem> {
    cells: BoardCells<TItem>;
    cellOrder: readonly BoardPlacement[];
    grabbed: BoardCellEntry<TItem>;
    selectedItemIds: ReadonlySet<string>;
    getLock?: (item: TItem) => BoardLock | null;
    hasMultiValueLanes: boolean;
}

/**
 * What picking up `grabbed` carries: the selected items with a card on the
 * board, when the grabbed card is one of them and another one moves with
 * it. Null for a single-card drag.
 */
export const getBoardDragGroup = <TItem>({
    cells,
    cellOrder,
    grabbed,
    selectedItemIds,
    getLock,
    hasMultiValueLanes,
}: GetBoardDragGroupArgs<TItem>): BoardDragGroup | null => {
    if (!selectedItemIds.has(grabbed.itemId)) {
        return null;
    }
    const itemIds = new Set<string>();
    const lockedItemIds = new Set<string>();
    getCopiesByItem(cells, cellOrder, selectedItemIds).forEach(
        ([copy], itemId) =>
            (getLock?.(copy.item) ? lockedItemIds : itemIds).add(itemId),
    );
    return itemIds.size < 2
        ? null
        : { itemIds, lockedItemIds, isLaneFixed: hasMultiValueLanes };
};

export interface BoardMoveEnds<TItem> {
    from: BoardCellEntry<TItem>;
    to: BoardPlacement;
}

/**
 * Where an item moves for a group drop on `target`: to the target column
 * and lane, or the target column in its own lane when lanes are fixed or
 * its copies sit in several lanes. It moves from, in order: its copy
 * already in that cell (so it stays put), the grabbed copy, its copy in
 * the target lane when it keeps its lane, or its first copy.
 */
export const getMoveEnds = <TItem>({
    copies,
    target,
    isLaneFixed,
    activeKey,
}: {
    copies: BoardItemCopies<TItem>;
    target: BoardPlacement;
    isLaneFixed: boolean;
    /** The grabbed card's placement key. */
    activeKey: string;
}): BoardMoveEnds<TItem> => {
    const [firstCopy] = copies;
    const isLaneKept =
        isLaneFixed ||
        copies.some(
            (copy) => copy.placement.laneId !== firstCopy.placement.laneId,
        );
    const findCopyIn = (cell: BoardPlacement) =>
        copies.find((copy) => getCellKey(copy.placement) === getCellKey(cell));
    const picked =
        findCopyIn(target) ??
        copies.find((copy) => copy.placementKey === activeKey) ??
        (isLaneKept
            ? copies.find((copy) => copy.placement.laneId === target.laneId)
            : undefined) ??
        firstCopy;
    const to = isLaneKept
        ? { columnId: target.columnId, laneId: picked.placement.laneId }
        : target;
    return { from: findCopyIn(to) ?? picked, to };
};

/** Why `canMove` refuses the move; undefined when it stays in its own cell. */
export const getMoveRefusal = <TItem>(
    { from, to }: BoardMoveEnds<TItem>,
    canMove: BoardMoveRule<TItem> | undefined,
) => {
    if (
        canMove === undefined ||
        getCellKey(from.placement) === getCellKey(to)
    ) {
        return undefined;
    }
    const verdict = canMove({ item: from.item, from: from.placement, to });
    return verdict.isAllowed ? undefined : verdict.reason;
};

interface GetGroupRefusalsArgs<TItem> {
    cells: BoardCells<TItem>;
    cellOrder: readonly BoardPlacement[];
    group: BoardDragGroup;
    /** The grabbed card's placement key. */
    activeKey: string;
    canMove?: BoardMoveRule<TItem>;
}

/**
 * Cells that refuse every card of the group, with the first card's reason.
 * Asked once at pick-up; each cell stops at the first card it accepts.
 */
export const getGroupRefusals = <TItem>({
    cells,
    cellOrder,
    group,
    activeKey,
    canMove,
}: GetGroupRefusalsArgs<TItem>): BoardRefusals => {
    if (canMove === undefined) {
        return NO_REFUSALS;
    }
    const copiesList = Array.from(
        getCopiesByItem(cells, cellOrder, group.itemIds).values(),
    );
    const getCellRefusal = (target: BoardPlacement) => {
        let firstReason: string | undefined;
        for (const copies of copiesList) {
            const reason = getMoveRefusal(
                getMoveEnds({
                    copies,
                    target,
                    isLaneFixed: group.isLaneFixed,
                    activeKey,
                }),
                canMove,
            );
            if (reason === undefined) {
                return undefined;
            }
            firstReason ??= reason;
        }
        return firstReason;
    };
    const refusals = new Map<string, string>();
    cellOrder.forEach((target) => {
        const reason = getCellRefusal(target);
        if (reason !== undefined) {
            refusals.set(getCellKey(target), reason);
        }
    });
    return refusals;
};
